import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode } from 'primereact/api';
import { addLocale, locale } from 'primereact/api';
import { Column } from 'primereact/column';
import {
  DataTable,
  DataTableFilterMetaData,
  DataTablePageEvent,
  DataTableSortMeta,
  DataTableStateEvent
} from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';

import 'primeicons/primeicons.css';
import 'primeflex/primeflex.css';

import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import TablePageHeader from 'ui/shared/TablePageHeader';

import { GeographicalZoneService } from '../components/services/GeographicalZoneService';
import { GeographicalZoneTemplate } from '../constants/GeographicalZoneTemplate';
import { isSelected } from '../helpers';
import { useDebounce } from '../hooks';
import { ruLocale } from '../lang/ruLocale';
import { GeographicalZone, ObjectForNameSelection } from '../types/interfaces';
import GeographicalZoneCard from '../ui/GeographicalZoneCard';
import { clearFilter } from '../utils';
import VisibilityWrapper from '../utils/wrappers/VisibilityWrapper';

export default function GeographicalZones() {
  addLocale('ru', ruLocale.ru);
  locale('ru');

  const [geographicalZones, setGeographicalZones] = useState<GeographicalZone[]>(
    []
  );
  const [dialogVisible, setDialogVisible] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState(100);
  const [pageNumber, setPageNumber] = useState(1);
  const [first, setFirst] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [deleteDialogVisible, setDeleteDialogVisible] =
    useState<boolean>(false);
  const [geographicalZoneToDelete, setGeographicalZoneToDelete] = useState<
    string | number | null
  >(null);
  const [selectedGeographicalZone, setSelectedGeographicalZone] =
    useState<GeographicalZone>(GeographicalZoneTemplate);
  const [filters, setFilters] = useState<
    Record<string, DataTableFilterMetaData>
  >({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
    description: { value: null, matchMode: FilterMatchMode.CONTAINS },
    avgTransportSpeed: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });

  const [multiSortMeta, setMultiSortMeta] = useState<
    DataTableSortMeta[] | undefined | null
  >(undefined);

  const setMultiSortMetaCallback = useCallback((e: DataTableStateEvent) => {
    setLoading(true);
    setMultiSortMeta(e.multiSortMeta);
    setPageNumber(1);
    setFirst(0);
  }, []);

  const [loading, setLoading] = useState<boolean>(true);
  const nameFilterRef = useRef<HTMLInputElement>(null);
  const descriptionFilterRef = useRef<HTMLInputElement>(null);
  const avgTransportSpeedFilterRef = useRef<HTMLInputElement>(null);
  const toast = useRef<Toast>(null);
  const geographicalZoneCardRef = useRef<null | { handleCancel: () => void }>(
    null
  );

  const isSelectedIdExist = isSelected(selectedGeographicalZone);

  const onPageChanged = useCallback((event: DataTablePageEvent) => {
    if (event.rows !== undefined) {
      setPageSize(event.rows);
      setFirst(0);
    }
    if (event.page !== undefined) {
      setFirst(event.first);
      setPageNumber(event.first / event.rows + 1);
    }
  }, []);

  const loadData = useCallback(() => {
    setLoading(true);
    const sort = multiSortMeta?.[0];
    const payload = JSON.stringify([
      {
        field: 'name',
        value: filters.name?.value,
        operator: filters.name?.matchMode
      },
      {
        field: 'description',
        value: filters.description?.value,
        operator: filters.description?.matchMode
      },
      {
        field: 'avgTransportSpeed',
        value: filters.avgTransportSpeed?.value,
        operator: filters.avgTransportSpeed?.matchMode
      },
      {
        field: 'global',
        value: filters.global?.value,
        operator: filters.global?.matchMode
      },
      {
        field: 'avgTransportSpeed',
        value: filters.avgTransportSpeed?.value,
        operator: filters.avgTransportSpeed?.matchMode
      }
    ]);

    GeographicalZoneService.getGeographicalZones({
      pageNumber,
      pageSize,
      sortBy: sort?.field,
      sortDirection: sort?.order === 1 ? 'asc' : 'desc',
      filters: payload
    }).then((resp) => {
      if (resp.data) {
        setGeographicalZones(resp.data.itemsList);
        setTotalCount(resp.data.total || 0);
      } else {
        setGeographicalZones([]);
        setTotalCount(0);
      }
      setLoading(false);
    });
  }, [filters, multiSortMeta, pageNumber, pageSize]);

  const { debouncedCallback: debouncedLoadData } = useDebounce(loadData, 300);

  useEffect(() => {
    debouncedLoadData();
  }, [debouncedLoadData, filters, multiSortMeta, pageNumber, pageSize]);

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setPageNumber(1);
    setFirst(0);
    setFilters(_filters);
  };

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange =
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    };

  const addButtonHandle = () => {
    setSelectedGeographicalZone(GeographicalZoneTemplate);
    setDialogVisible(true);
  };

  const handleDeleteConfirmation = () => {
    setSelectedGeographicalZone(GeographicalZoneTemplate);
    handleDelete(geographicalZoneToDelete);
    setDeleteDialogVisible(false);
  };

  const onFiltersChange = useCallback((e: DataTableStateEvent) => {
    const next = e.filters as Record<string, DataTableFilterMetaData>;
    setPageNumber(1);
    setFirst(0);
    setFilters(next);
  }, []);

  const handleUpdate = async (id: number, updatedObject: GeographicalZone) => {
    const result = await GeographicalZoneService.updateGeographicalZone(
      id,
      updatedObject
    );
    loadData();
    return result;
  };

  const handleDelete = async (id: string | number | null) => {
    if (id) {
      await GeographicalZoneService.deleteGeographicalZone(
        parseInt(id.toString(), 10)
      );
      setSelectedGeographicalZone(GeographicalZoneTemplate);
      loadData();
    }
  };

  const handleAdd = async (geographicalZone: GeographicalZone) => {
    const result =
      await GeographicalZoneService.createGeographicalZone(geographicalZone);
    loadData();
    return result;
  };

  const closeDialog = () => {
    // Function to close the dialog in parent
    setDialogVisible(false);
  };

  const onHideCard = () => {
    if (geographicalZoneCardRef.current !== null) {
      geographicalZoneCardRef.current.handleCancel();
    }
  };

  const editButtonHandle = () => {
    if (toast.current && !isSelectedIdExist) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите географическую зону для редактирования',
        life: 3000
      });
    }
    if (isSelectedIdExist) setDialogVisible(true);
  };

  const deleteButtonHandle = () => {
    // If a valid process is selected, render the deletion confirmation dialog.
    if (selectedGeographicalZone?.id) {
      setGeographicalZoneToDelete(selectedGeographicalZone.id);
      setDeleteDialogVisible(true);
    }
    if (toast.current && !selectedGeographicalZone?.id) {
      toast.current.show({
        severity: 'warn',
        summary: 'Ошибка',
        detail: 'Выберите географическую зону для удаления',
        life: 3000
      });
    }
  };

  const header = (  
    <TablePageHeader
      tableName="geographicalZones"
      onGlobalSearchChange={value =>
        handleInputChange('global')({ target: { value } } as React.ChangeEvent<HTMLInputElement>)
      }
      onAdd={addButtonHandle}
      onEdit={editButtonHandle}
      onDelete={deleteButtonHandle}
    />
  )

  const tableElement = document.querySelector('.p-datatable-thead');

  function offset(el: Element) {
    var rect = el.getBoundingClientRect(),
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    return { top: rect.top + scrollTop };
  }

  const scrollHeight = useMemo(() => {
    const paginatorElement = document.querySelector('.p-paginator');
    const paginatorHeight =
      paginatorElement instanceof HTMLElement
        ? paginatorElement.offsetHeight
        : 0;
    const tableOffset =
      tableElement instanceof HTMLElement ? offset(tableElement).top : 0;
    return `calc(100vh - ${
      tableElement instanceof HTMLElement ? tableOffset + paginatorHeight : 0
    }px)`;
  }, [tableElement]);

  return (
    <div className="geographicalZones-table wrapper wrapper--main">
      <Toast ref={toast} />
      <Dialog
        header="Настройка географической зоны"
        visible={dialogVisible}
        style={{ minWidth: '630px', width: '630px', minHeight: 'fit-content' }}
        breakpoints={{ '960px': '75vw', '641px': '100vw' }}
        maximizable
        modal
        contentStyle={{ minHeight: '300px' }}
        onHide={() => onHideCard()}
      >
        <VisibilityWrapper code="geographicalZone-card__roles">
          <GeographicalZoneCard
            geographicalZoneProps={selectedGeographicalZone}
            onUpdate={handleUpdate}
            onDelete={async (id) => {
              await handleDelete(id);
            }}
            onAdd={handleAdd}
            closeDialog={closeDialog}
            ref={geographicalZoneCardRef}
          />
        </VisibilityWrapper>
      </Dialog>

      <DeleteConfirmationDialog
        visible={deleteDialogVisible}
        onHide={() => setDeleteDialogVisible(false)}
        onConfirm={handleDeleteConfirmation}
        entityType="географическую зону"
        entityName={selectedGeographicalZone?.name}
      />
      <DataTable
        filters={filters}
        lazy
        multiSortMeta={multiSortMeta}
        onSort={setMultiSortMetaCallback}
        selectionMode="single"
        selection={selectedGeographicalZone!}
        onSelectionChange={(e) => setSelectedGeographicalZone(e.value)}
        onRowDoubleClick={(e) => {
          setDialogVisible(true);
          setSelectedGeographicalZone(e.data as GeographicalZone);
        }}
        metaKeySelection={false}
        scrollable
        paginator
        first={first}
        rows={pageSize}
        totalRecords={totalCount}
        rowsPerPageOptions={[10, 25, 50, 100]}
        onPage={onPageChanged}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink JumpToPageInput"
        currentPageReportTemplate="{currentPage} из {totalPages}"
        paginatorLeft={' '}
        paginatorRight={`Всего: ${geographicalZones.length > 0 ? totalCount : 0}`}
        sortMode="multiple"
        removableSort
        scrollHeight={scrollHeight}
        size="small"
        resizableColumns
        reorderableColumns
        reorderableRows
        value={geographicalZones}
        dataKey="id"
        filterDisplay="row"
        loading={loading}
        header={header}
        emptyMessage="Географические зоны не найдены."
        onRowReorder={(e) => setGeographicalZones(e.value)}
        tableStyle={{ minWidth: '50rem' }}
        onFilter={onFiltersChange}
      >
        <Column
          columnKey="name"
          sortable
          field="name"
          header="Название"
          filter
          onFilterClear={() =>
            clearFilter('name', filters, setFilters, nameFilterRef)
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={nameFilterRef}
                onChange={handleInputChange('name')}
                placeholder="Поиск по названию"
              />
            </div>
          }
          style={{ width: '22%' }}
        />
        <Column
          columnKey="description"
          sortable
          field="description"
          sortField="description"
          header="Описание"
          showClearButton={true}
          filter
          onFilterClear={() =>
            clearFilter(
              'description',
              filters,
              setFilters,
              descriptionFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={descriptionFilterRef}
                onChange={handleInputChange('description')}
                placeholder="Поиск по описанию"
              />
            </div>
          }
          style={{ width: '22%' }}
        />
        <Column
          columnKey="avgTransportSpeed"
          sortable
          field="avgTransportSpeed"
          sortField="avgTransportSpeed"
          header="Средняя скорость движения"
          showClearButton={true}
          filter
          body={(rowData: {avgTransportSpeed?: ObjectForNameSelection | null}) => rowData.avgTransportSpeed?.name ?? ''}
          onFilterClear={() =>
            clearFilter(
              'avgTransportSpeed',
              filters,
              setFilters,
              avgTransportSpeedFilterRef
            )
          }
          filterElement={
            <div className="flex justify-content-end">
              <InputText
                ref={avgTransportSpeedFilterRef}
                onChange={handleInputChange('avgTransportSpeed')}
                placeholder="Поиск по средней скорости"
              />
            </div>
          }
          style={{ width: '22%' }}
        />
      </DataTable>
    </div>
  );
}
